import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type {
  SuggestionTemplate,
  SuggestionType,
} from "@/lib/suggestions/service";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";

export type { SuggestionTemplate, SuggestionType };

/** Debounces a fast-changing value (default 300ms). */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Fetches history-based entry templates from /api/suggestions.
 *
 * `q` is debounced 300ms internally, so callers can pass the raw input value.
 * Shared by QuickAddSheet (template chips + typeahead), IncomeForm and
 * ExpenseForm (description typeahead) — never reimplement the fetch per
 * surface.
 */
export function useEntrySuggestions(
  type: SuggestionType,
  q: string,
  enabled = true,
) {
  const debouncedQ = useDebouncedValue(q.trim(), 300);

  return useQuery<SuggestionTemplate[]>({
    queryKey: ["suggestions", type, debouncedQ],
    queryFn: async () => {
      const params = new URLSearchParams({ type });
      if (debouncedQ) params.set("q", debouncedQ);
      const res = await apiFetch(`${apiUrl("suggestions")}?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load suggestions");
      return res.json();
    },
    enabled,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}
